"use client";

import Link from "next/link";
import Button from "../ui/Button";
import Image from "next/image";
import TrophyIcon from "../icons/TrophyIcon";
import DeliveryTruckIcon from "../icons/DeliveryTruckIcon";
import TagIcon from "../icons/TagIcon";
import FlowerIcon from "../icons/FlowerIcon";
import GiftsIcon from "../icons/GiftsIcon";
import BuildingIcon from "../icons/BuildingIcon";
import BenefitCard from "../ui/BenefitCard";

export type BenefitItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const DEFAULT_BENEFITS: BenefitItem[] = [
  {
    icon: <TrophyIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Premium Selection",
    description:
      "We offer premium cannabis products, accessories, & gear carefully curated for every single consumer",
  },
  {
    icon: <DeliveryTruckIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Fast Delivery",
    description: "Get same-day weed delivery in Calgary, arriving at your door in just 45 minutes",
  },
  {
    icon: <TagIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Best Prices",
    description: "Fair prices on top cannabis products, online or in-store",
  },
  {
    icon: <FlowerIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Fresh Drops",
    description:
      "Discover the hottest new cannabis strains, edibles, and concentrates arriving on our shelves regularly",
  },
  {
    icon: <GiftsIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Dank Rewards",
    description: "Join Dank Club to earn points, unlock rewards, and enjoy exclusive daily special deals",
  },
  {
    icon: <BuildingIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Alberta Shipping",
    description:
      "We provide secure mail-order marijuana shipping across Alberta, delivering products discreetly",
  },
];

export type BenefitsSectionProps = {
  /** Anchor id so other sections can deep-link here. */
  id?: string;
  heading?: string;
  /** Six-card grid contents; defaults to the dispensary benefits used on the homepage. */
  benefits?: BenefitItem[];
  /** Hide the "Shop now" header/footer buttons (e.g. Dank Club member benefits). */
  showShopButtons?: boolean;
  /** Extra classes on the inner container (e.g. `!px-0` when the page supplies its own padding). */
  contentClassName?: string;
};

export default function BenefitsSection({
  id,
  heading = "Benefits of Dank cannabis dispensary",
  benefits = DEFAULT_BENEFITS,
  showShopButtons = true,
  contentClassName,
}: BenefitsSectionProps = {}) {
  return (
    <section
      id={id}
      className="w-full"
      aria-label={heading}
    >
      <div className={`w-full relative px-4 sm:px-8${contentClassName ? ` ${contentClassName}` : ''}`}>
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between sm:mb-8 mb-4 sm:gap-4 gap-2">
          <h2 className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary">
            {heading}
          </h2>
          {showShopButtons ? (
            <Button asChild variant="primary" size="lg" className="w-fit px-6 py-3 rounded-xl max-sm:hidden">
              <Link href="/search">
                Shop now
              </Link>
            </Button>
          ) : null}
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 sm:gap-4 gap-2">
          {benefits.map((benefit) => (
            <BenefitCard
              key={benefit.title}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>

        {showShopButtons ? (
          <Button asChild variant="primary" size="lg" className="w-full px-6 py-3 rounded-xl mt-6 sm:hidden mt-4">
            <Link href="/search">
              Shop now
            </Link>
          </Button>
        ) : null}

        {/* CTA Banner */}
        <div className="mt-6 relative w-full rounded-2xl overflow-hidden bg-[#FFD2D2]">
          {/* Background Shape */}
          <div className="absolute inset-0">
            <Image
              src="/media/sections/lifestyle/backgrounds/banner-background-shape.webp"
              alt=""
              fill
              className="object-cover"
              aria-hidden="true"
            />
          </div>

          {/* Content Overlay */}
          <div className="relative z-10 sm:px-6 px-4 flex flex-col lg:flex-row items-center justify-between gap-4 sm:pb-6 pb-4">
            <div className="h-48 w-full overflow-hidden flex items-center justify-center sm:hidden">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="w-full object-center"
                aria-hidden="true"
              />
            </div>
            <div className="h-32 overflow-hidden max-sm:hidden -mb-8">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="object-top -mt-6"
                aria-hidden="true"
              />
            </div>
            {/* Text Content */}
            <div className="flex-1 sm:mt-7">
              <h2 className="text-body-20 font-bold text-text-primary mb-1 leading-tight">
                Enjoy a high country life
              </h2>
              <p className="text-body-16 font-medium text-text-secondary leading-relaxed">
                Gummies, chocolates and drinks from Canada&apos;s best makers, ready for pickup or same day delivery.
              </p>
            </div>

            {/* Button */}
            <Button asChild variant="primary" size="lg" className="sm:w-fit w-full px-6 py-3 rounded-xl mt-6">
              <Link href="/search?category=Edibles">
                Shop our new edibles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
